import type { SubscribeMessageTemplates } from './subscribe-message'

export const brandThemes = ['felt', 'gold', 'amber', 'peach', 'blue'] as const
export const clientThemes = [
  'green',
  'violet',
  'gold',
  'gilt',
  'orange',
  'peach',
  'postmodern',
] as const
export const tenantProfiles = ['standalone', 'integrated'] as const
export const miniappProfileIds = [
  'goldenfire',
  'feltos',
  'horizon',
  'jiawen',
  'vantory',
  'fullpot',
  'hustler',
] as const

export const cardTextures = ['seigaiha', 'damask', 'chips', 'organic'] as const
export type CardTexture = (typeof cardTextures)[number]
export const defaultCardTexture: CardTexture = 'seigaiha'

export type BrandTheme = (typeof brandThemes)[number]
export type ClientTheme = (typeof clientThemes)[number]
export type TenantProfile = (typeof tenantProfiles)[number]
export type MiniappProfileId = (typeof miniappProfileIds)[number]

export interface MiniappProfile {
  id: MiniappProfileId
  appid: string
  projectName: string
  hostname: string
  hosts: string[]
  access: TenantProfile
  clientTheme: ClientTheme
  /** 墨色卡与页底纹样，缺省青海波 */
  cardTexture?: CardTexture
  subscribeMessages?: SubscribeMessageTemplates
}

/** release 走线上 api.{主域}，develop / trial 走 dev.{主域}。 */
export function profileApiOrigin(hostname: string, wechatEnv?: string) {
  const prefix = wechatEnv === 'release' ? 'api' : 'dev'
  return `https://${prefix}.${hostname}`
}

/** release 走租户主域（后台与微信支付回调），其余走 dev.{主域}。 */
export function profileAppOrigin(hostname: string, wechatEnv?: string) {
  if (wechatEnv === 'release') return `https://${hostname}`
  return `https://dev.${hostname}`
}

export function normalizeHostname(value: string | null | undefined) {
  const candidate = value?.split(',')[0]?.trim().toLowerCase()
  if (!candidate || /[\\/\s]/.test(candidate)) return null

  try {
    const url = new URL(`http://${candidate}`)
    if (url.username || url.password || url.search || url.hash) return null
    return url.hostname.replace(/^\[|\]$/g, '').replace(/\.$/, '') || null
  } catch {
    return null
  }
}

const hostedPrefixes = ['api.', 'dev.'] as const

/**
 * 小程序 API 与开发入口归到租户主域。
 * 画像 hosts 没覆盖的子域（例如 hustler.ifelt.cn）仍能落到同一租户。
 */
export function tenantApexHostname(hostname: string) {
  const host = hostname.toLowerCase()
  const prefix = hostedPrefixes.find((item) => host.startsWith(item))
  if (!prefix) return host
  const apex = host.slice(prefix.length)
  return apex.includes('.') ? apex : host
}

export function findMiniappProfileByHost(
  hostname: string,
  profiles: MiniappProfile[],
) {
  const host = hostname.toLowerCase()
  return (
    profiles.find(
      (profile) =>
        profile.hostname === host ||
        profile.hosts.some((item) => item.toLowerCase() === host),
    ) ?? null
  )
}

export function findMiniappProfileByAppId(
  appid: string,
  profiles: MiniappProfile[],
) {
  return profiles.find((profile) => profile.appid === appid) ?? null
}
